import { useAuthStore } from "@/stores/authStore"

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3021/api"

let onUnauthorizedCallback: (() => void) | null = null

export function setUnauthorizedHandler(handler: () => void) {
	onUnauthorizedCallback = handler
}

function getAuthToken(): string | null {
	return useAuthStore.getState().token
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
	const token = getAuthToken()
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		...(options.headers as Record<string, string>),
	}
	if (token) headers["Authorization"] = `Bearer ${token}`

	const response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers })

	if (response.status === 401) {
		onUnauthorizedCallback?.()
		throw new Error("Unauthorized")
	}

	if (!response.ok) {
		let errorMessage = `Request failed (${response.status})`
		try {
			const data = await response.json()
			if (data.error) errorMessage = data.error
		} catch {
			// Non-JSON response (e.g., HTML from reverse proxy)
		}
		throw new Error(errorMessage)
	}

	const data = await response.json()
	return data as T
}

async function uploadRequest<T>(path: string, formData: FormData): Promise<T> {
	const token = getAuthToken()
	const headers: Record<string, string> = {}
	if (token) headers["Authorization"] = `Bearer ${token}`

	const response = await fetch(`${API_BASE_URL}${path}`, {
		method: "POST",
		headers,
		body: formData,
	})

	if (response.status === 401) {
		onUnauthorizedCallback?.()
		throw new Error("Unauthorized")
	}

	const data = await response.json()
	if (!response.ok) throw new Error(data.error || "Upload failed")
	return data as T
}

export const api = {
	get: <T>(path: string) => request<T>(path),
	post: <T>(path: string, body: unknown) =>
		request<T>(path, { method: "POST", body: JSON.stringify(body) }),
	put: <T>(path: string, body: unknown) =>
		request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
	patch: <T>(path: string, body: unknown) =>
		request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
	delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
	upload: <T>(path: string, formData: FormData) => uploadRequest<T>(path, formData),
}
